'use client';

import { useCallback, useMemo, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import type { GeoJSON } from 'geojson';
import {
  ArrowRight,
  ChevronDown,
  Clock4,
  LocateFixed,
  MapPin,
  Navigation,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import type {
  BusinessLocationsMarker,
  BusinessLocationsRoute,
} from '@/components/maps/BusinessLocationsMap';

/**
 * The interactive half of `/business/[slug]`: every branch of one business as a
 * numbered list beside one map, with in-app directions to each.
 *
 * The list, not the map, is the primary control. Every location is reachable
 * without WebGL, without a mouse and without JavaScript: the address, the order
 * link and a plain Google Maps fallback are all real anchors in the list. The
 * map is loaded lazily (`ssr: false`) so MapLibre never lands in the first
 * paint, and it is decoration on top of a complete list, never the only path.
 *
 * Directions prefer OUR routing (`/api/public/directions`, Mapbox→OSRM) so a
 * guest gets the distance and the drawn route inside Menuraq rather than being
 * bounced to Google. "Open in Maps" stays as a secondary link for voice
 * turn-by-turn, not as the only way to get there.
 */

const BusinessLocationsMap = dynamic(() => import('@/components/maps/BusinessLocationsMap'), {
  ssr: false,
  loading: () => (
    <div className="flex h-full w-full items-center justify-center bg-muted/40 text-sm text-muted-foreground">
      Loading map…
    </div>
  ),
});

export type BusinessLocation = {
  id: string;
  name: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  openState: 'open' | 'closed' | 'unknown';
  isDefault: boolean;
  orderHref: string;
};

type TravelMode = 'driving' | 'walking' | 'cycling';

type RouteState = {
  locationId: string;
  geometry: GeoJSON;
  mode: TravelMode;
  distanceMeters: number;
  durationSeconds: number;
  steps: Array<{ instruction: string; distance: string }>;
};

type DirectionsResponse = {
  ok?: boolean;
  error?: string;
  routes?: Array<{
    coords: [number, number][];
    distanceMeters: number;
    durationSeconds: number;
    steps?: Array<{ instruction: string; distance: string; duration?: string }>;
  }>;
};

const MODES: Array<{ id: TravelMode; label: string }> = [
  { id: 'driving', label: 'Drive' },
  { id: 'walking', label: 'Walk' },
  { id: 'cycling', label: 'Cycle' },
];

function formatDistance(meters: number): string {
  if (!Number.isFinite(meters)) return '';
  return meters < 1000 ? `${Math.max(1, Math.round(meters))} m` : `${(meters / 1000).toFixed(1)} km`;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  if (seconds < 60) return '<1 min';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder > 0 ? `${hours} h ${remainder} min` : `${hours} h`;
}

/** A Google Maps directions link, used only as the always-available fallback. */
function googleMapsHref(
  lat: number,
  lng: number,
  mode: TravelMode,
  origin: { lat: number; lng: number } | null,
): string {
  const params = new URLSearchParams({
    api: '1',
    destination: `${lat},${lng}`,
    travelmode: mode,
  });
  if (origin) params.set('origin', `${origin.lat},${origin.lng}`);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function requestUserLocation(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  });
}

function OpenStatePill({ state }: { state: BusinessLocation['openState'] }) {
  if (state === 'unknown') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
        <Clock4 className="h-3 w-3" /> Hours unavailable
      </span>
    );
  }
  const open = state === 'open';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
        open
          ? 'bg-[var(--color-success)]/15 text-[var(--color-success)]'
          : 'bg-[var(--color-danger)]/15 text-[var(--color-danger)]',
      )}
    >
      <Clock4 className="h-3 w-3" /> {open ? 'Open now' : 'Closed'}
    </span>
  );
}

export default function BusinessLocations({ locations }: { locations: BusinessLocation[] }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<TravelMode>('driving');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [routeState, setRouteState] = useState<RouteState | null>(null);
  const [routing, setRouting] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);
  const [showSteps, setShowSteps] = useState(false);

  const markers = useMemo<BusinessLocationsMarker[]>(
    () =>
      locations
        .map((location, index) => ({ location, index }))
        .filter(({ location }) => location.lat != null && location.lng != null)
        .map(({ location, index }) => ({
          id: location.id,
          name: location.name,
          lat: location.lat as number,
          lng: location.lng as number,
          index: index + 1,
        })),
    [locations],
  );

  const fitSignal = useMemo(
    () => `${markers.map((m) => m.id).join(',')}|${routeState?.locationId ?? ''}`,
    [markers, routeState?.locationId],
  );

  const startDirections = useCallback(
    async (location: BusinessLocation, nextMode: TravelMode) => {
      if (location.lat == null || location.lng == null) return;
      setSelectedId(location.id);

      let origin = userLocation;
      if (!origin) {
        origin = await requestUserLocation();
        if (!origin) {
          setRouteError('Allow location access to get directions from where you are.');
          return;
        }
        setUserLocation(origin);
      }

      setRouting(true);
      setRouteError(null);
      try {
        const res = await fetch('/api/public/directions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            originLat: origin.lat,
            originLng: origin.lng,
            destLat: location.lat,
            destLng: location.lng,
            mode: nextMode,
          }),
        });
        const data = (await res.json().catch(() => null)) as DirectionsResponse | null;
        const route = data?.ok ? data.routes?.[0] : null;
        if (res.ok && route) {
          const geometry = {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: route.coords.map(([lat, lng]) => [lng, lat]),
            },
          } as unknown as GeoJSON;
          setRouteState({
            locationId: location.id,
            geometry,
            mode: nextMode,
            distanceMeters: route.distanceMeters,
            durationSeconds: route.durationSeconds,
            steps: (route.steps ?? []).map((step) => ({
              instruction: step.instruction,
              distance: step.distance,
            })),
          });
          setShowSteps(false);
        } else {
          setRouteError(data?.error || 'No route found to this location.');
        }
      } catch {
        setRouteError('Directions are unavailable right now. You can still open Google Maps.');
      } finally {
        setRouting(false);
      }
    },
    [userLocation],
  );

  const clearRoute = useCallback(() => {
    setRouteState(null);
    setRouteError(null);
    setShowSteps(false);
  }, []);

  const changeMode = useCallback(
    (nextMode: TravelMode) => {
      setMode(nextMode);
      const active = locations.find((location) => location.id === routeState?.locationId);
      if (active) void startDirections(active, nextMode);
    },
    [locations, routeState?.locationId, startDirections],
  );

  if (locations.length === 0) {
    return (
      <div className="rounded-[var(--pub-r-lg)] border border-dashed border-border/80 bg-muted/40 px-5 py-10 text-center text-sm text-muted-foreground">
        <MapPin className="mx-auto mb-2 h-5 w-5" />
        No locations are listed for this business yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {routeError ? (
        <p
          role="alert"
          className="flex items-start gap-2 rounded-[var(--pub-r-md)] border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 px-3 py-2.5 text-sm text-[var(--color-danger)]"
        >
          <X className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{routeError}</span>
        </p>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        <ol className="space-y-3">
          {locations.map((location, index) => {
            const hasCoords = location.lat != null && location.lng != null;
            const isActive = routeState?.locationId === location.id;
            return (
              <li key={location.id}>
                <article
                  className={cn(
                    'rounded-[var(--pub-r-lg)] border bg-card p-4 shadow-sm transition-colors',
                    selectedId === location.id ? 'border-[var(--color-accent)]' : 'border-border/70',
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)]/15 text-xs font-bold text-[var(--color-accent)]"
                    >
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold tracking-tight">{location.name}</h3>
                        {location.isDefault ? (
                          <span className="rounded-full border border-border/70 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                            Main
                          </span>
                        ) : null}
                        <OpenStatePill state={location.openState} />
                      </div>
                      {location.address ? (
                        <p className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground">
                          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span className="min-w-0 break-words">{location.address}</span>
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 pl-10">
                    <Button asChild size="sm" className="gap-1.5">
                      <Link href={location.orderHref}>
                        Order
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                    {hasCoords ? (
                      <Button
                        type="button"
                        size="sm"
                        variant={isActive ? 'default' : 'outline'}
                        className="gap-1.5"
                        disabled={routing && isActive}
                        onClick={() => void startDirections(location, mode)}
                      >
                        <Navigation className="h-4 w-4" />
                        {routing && isActive ? 'Finding route…' : 'Directions'}
                      </Button>
                    ) : null}
                    {hasCoords ? (
                      <Button asChild size="sm" variant="ghost" className="gap-1.5">
                        <a
                          href={googleMapsHref(location.lat as number, location.lng as number, mode, userLocation)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <LocateFixed className="h-4 w-4" />
                          Open in Maps
                        </a>
                      </Button>
                    ) : null}
                  </div>

                  {isActive && routeState ? (
                    <div className="mt-3 rounded-[var(--pub-r-md)] border border-border/70 bg-muted/40 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          {MODES.map((option) => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => changeMode(option.id)}
                              aria-pressed={mode === option.id}
                              className={cn(
                                'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                                mode === option.id
                                  ? 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)]'
                                  : 'bg-background text-muted-foreground hover:text-foreground',
                              )}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={clearRoute}
                          className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-3.5 w-3.5" /> Clear
                        </button>
                      </div>

                      <p className="mt-2 text-sm font-semibold">
                        {formatDistance(routeState.distanceMeters)}
                        {formatDuration(routeState.durationSeconds)
                          ? ` · ${formatDuration(routeState.durationSeconds)}`
                          : ''}
                        <span className="ml-1 font-normal text-muted-foreground">by {mode}</span>
                      </p>

                      {routeState.steps.length > 0 ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setShowSteps((value) => !value)}
                            aria-expanded={showSteps}
                            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[var(--color-accent)]"
                          >
                            <ChevronDown
                              className={cn('h-3.5 w-3.5 transition-transform', showSteps && 'rotate-180')}
                            />
                            {showSteps ? 'Hide steps' : `Show ${routeState.steps.length} steps`}
                          </button>
                          {showSteps ? (
                            <ol className="mt-2 space-y-1.5">
                              {routeState.steps.map((step, stepIndex) => (
                                <li key={stepIndex} className="flex gap-2 text-xs text-muted-foreground">
                                  <span className="shrink-0 font-semibold text-foreground">
                                    {stepIndex + 1}.
                                  </span>
                                  <span className="min-w-0">
                                    {step.instruction}
                                    {step.distance ? (
                                      <span className="text-muted-foreground/80"> · {step.distance}</span>
                                    ) : null}
                                  </span>
                                </li>
                              ))}
                            </ol>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </article>
              </li>
            );
          })}
        </ol>

        {markers.length > 0 ? (
          <div className="lg:sticky lg:top-20 lg:self-start">
            <div className="h-[320px] overflow-hidden rounded-[var(--pub-r-lg)] border border-border/70 shadow-sm sm:h-[380px] lg:h-[560px]">
              <BusinessLocationsMap
                markers={markers}
                selectedId={selectedId}
                onSelect={setSelectedId}
                route={routeState ? { geometry: routeState.geometry, mode: routeState.mode } : null}
                userLocation={userLocation}
                fitSignal={fitSignal}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
